import React, { createRef, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Header from "./Header";
import Settings from "./Setting";
import MediaPlayer from "./MediaPlayer";
// import TimedTextEditor from "./TimedTextEditor";
import COLORS from "../../../assets/Colors";
import { secondsToTimecode } from "../TimeCodeConverter/secondsToTimecodeNew";
import VideoPlayer from "../PlayerControls/VideoPlayer";
import theme from "../../../assets/theme";
import Export from "./Exports";
import SlateEditor from "../SlateEditor/MetaLive";
import SlateEditorTranscript from "../SlateEditor/index";
import { Images } from "../../../assets/images";
import SearchBar from "../../common/CustomTable/common/SearchBar";
import useTranscriptSearch from "../../../hooks/useTranscriptSearch";

const TranscriptEditor = React.memo(
  ({
    isEditable,
    mediaType,
    mediaUrl,
    data,
    setData,
    caseId,
    meetingId,
    transcriptId,
    title,
    refreshDetails,
    render,
    setRender,
    trigger,
    streamingKey,
    meetingType = "edited",
  }) => {
    const [showSettings, setShowSettings] = useState(false);
    const [showShortcuts, setShowShortCuts] = useState(false);
    const [showExportOptions, setShowExportOptions] = useState(false);
    const [isPauseWhileTypingOn, setIsPauseWhileTypingOn] = useState(true);
    const [isScrollIntoViewOn, setIsScrollIntoViewOn] = useState(false);
    const [rollBackValueInSeconds, setRollBackValueInSeconds] = useState(15);
    const [timecodeOffset, setTimecodeOffset] = useState(0);
    const [showTimecodes, setShowTimecodes] = useState(true);
    const [showSpeakers, setShowSpeakers] = useState(true);
    const [previewIsDisplayed, setPreviewIsDisplayed] = useState(true);
    const [mediaDuration, setMediaDuration] = useState("00:00:00:00");
    const [currentTime, setCurrentTime] = useState(0);
    const [searchText, setSearchText] = useState("");
    const transcriptEditorRef = createRef();
    const videoRef = React.useRef();
    const [hookSeek, setHookSeek] = useState();
    const autoScrollRef = useRef(null);
    const [previousTime, setPreviousTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    // Search functionality
    const {
      matches,
      currentMatchIndex,
      currentMatch,
      matchCount,
      nextMatch,
      prevMatch,
      reset: resetSearch,
      isSearchActive,
      isParagraphVisible,
      isFiltering,
    } = useTranscriptSearch(searchText, data);

    useEffect(() => {
      setTimeout(() => {
        if (autoScrollRef?.current) {
          autoScrollRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 500); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshDetails, trigger]);

    const onLoadedDataGetDuration = (e) => {
      const currentDuration = e.target.duration;
      const currentDurationWithOffset = currentDuration + timecodeOffset;
      const durationInSeconds = secondsToTimecode(currentDurationWithOffset);

      setMediaDuration(durationInSeconds);
    };

    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = previousTime;
        if (isPlaying?.isPlaying) {
          setIsPlaying(false);
          videoRef.current.pause();
        } else {
          setIsPlaying(false);
          videoRef.current.pause();
        }
      } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaUrl]);

    const handleTimeUpdate = (e) => {
      setPreviousTime(currentTime);
      const currentTimes = videoRef.current.currentTime;
      setCurrentTime(currentTimes);
    };

    const handlePauseWhileTyping = (e) => {
      setIsPauseWhileTypingOn(e?.target?.checked);
    };

    const handleRollBackValueInSeconds = (e) => {
      setRollBackValueInSeconds(e?.target?.value);
    };

    const handleShowTimecodes = (e) => {
      setShowTimecodes(e?.target?.checked);
    };

    const handleShowSpeakers = (e) => {
      setShowSpeakers(e?.target?.checked);
    };

    const handleSetTimecodeOffset = (timecodeOffset) => {
      setTimecodeOffset({ timecodeOffset: timecodeOffset }, () => {
        transcriptEditorRef.current.forceUpdate();
      });
    };

    const handlePreviewIsDisplayed = () => {
      setPreviewIsDisplayed({
        previewIsDisplayed: !this.state.previewIsDisplayed,
      });
    };

    const handleIsScrollIntoViewChange = (e) => {
      setIsScrollIntoViewOn(e?.target?.checked);
    };

    const handleWordClick = (startTime) => {
      setHookSeek(startTime);
    };

    const videoPlayer = (
      <VideoPlayer
        {...{
          mediaUrl,
          previewIsDisplayed,
          onLoadedDataGetDuration,
          videoRef,
        }}
        onTimeUpdate={handleTimeUpdate}
      />
    );

    const settings = (
      <Settings
        {...{
          showSettings,
          setShowSettings,
          isPauseWhileTypingOn,
          isScrollIntoViewOn,
          rollBackValueInSeconds,
          timecodeOffset,
          showTimecodes,
          showSpeakers,
          previewIsDisplayed,
          handlePauseWhileTyping,
          handleIsScrollIntoViewChange,
          handleRollBackValueInSeconds,
          handleSetTimecodeOffset,
          handleShowSpeakers,
          handlePreviewIsDisplayed,
          handleShowTimecodes,
          mediaType,
        }}
      />
    );

    const mediaControls = (
      <MediaPlayer
        {...{
          mediaDuration,
          mediaUrl,
          mediaType,
          currentTime,
          videoRef,
          rollBackValueInSeconds,
          timecodeOffset,
          caseId,
          title,
          setHookSeek,
          isPlaying,
          setIsPlaying,
        }}
        hookSeek={hookSeek}
        videoRef={videoRef}
      />
    );

    const exportOptions = (
      <Export
        {...{
          showExportOptions,
          setShowExportOptions,
          meetingId,
          transcriptId,
        }}
      />
    );

    return (
      <>
        <Container>
          <Watermark>
            <WatermarkLogo src={Images.companyLogo} alt="Client Logo" />
          </Watermark>
          {meetingType === "edited" ? (
            <Header
              {...{
                showSettings,
                showShortcuts,
                showExportOptions,
                setShowSettings,
                setShowShortCuts,
                setShowExportOptions,
                mediaUrl,
              }}
              settings={settings}
              exportOptions={exportOptions}
              mediaControls={videoRef?.current ? mediaControls : null}
            />
          ) : null}
          {meetingType === "edited" ? (
            <Grid>
              <Row style={{ display: mediaType === "audio" ? "none" : "" }}>
                <Aside>{videoPlayer}</Aside>
              </Row>
            </Grid>
          ) : null}
          {meetingType === "edited" ? (
            <SearchBarContainer>
              <SearchBar
                placeHolderText="Search transcript..."
                inputText={searchText}
                setInputText={setSearchText}
                backgroundColor="#f4f6ff"
                width="300px"
              />
              {isSearchActive && (
                <SearchInfo>
                  {matchCount > 0
                    ? `${currentMatchIndex + 1} of ${matchCount} matches`
                    : "No matches found"}
                </SearchInfo>
              )}
            </SearchBarContainer>
          ) : null}
          <Main isHeight={meetingType}>
            {/* <TimedTextEditor
                {...{
                  data,
                  setData,
                  mediaUrl,
                  mediaType,
                  timecodeOffset,
                  showTimecodes,
                  currentTime,
                  videoRef,
                  isEditable,
                  showSpeakers,
                  caseId,
                  mediaDuration,
                }}
                handleWordClick={handleWordClick}
              /> */}
            {meetingType === "edited" ? (
              <>
                <SlateEditorTranscript
                  {...{
                    data,
                    setData,
                    mediaUrl,
                    mediaType,
                    timecodeOffset,
                    showTimecodes,
                    currentTime,
                    videoRef,
                    isEditable,
                    showSpeakers,
                    caseId,
                    mediaDuration,
                    handleWordClick,
                    render,
                    setRender,
                    streamingKey,
                    searchText,
                    searchMatches: matches,
                    currentMatchIndex,
                    currentMatch,
                    isParagraphVisible,
                    isFiltering,
                  }}
                />
              </>
            ) : (
              <SlateEditor
                {...{
                  data,
                  setData,
                  mediaUrl,
                  mediaType,
                  timecodeOffset,
                  showTimecodes,
                  currentTime,
                  videoRef,
                  isEditable,
                  showSpeakers,
                  caseId,
                  mediaDuration,
                  handleWordClick,
                  render,
                  setRender,
                  streamingKey,
                  searchText,
                  searchMatches: matches,
                  currentMatchIndex,
                  currentMatch,
                  isParagraphVisible,
                  isFiltering,
                }}
              />
            )}
            <div ref={autoScrollRef} />
          </Main>
        </Container>
      </>
    );
  }
);

export default TranscriptEditor;

const Container = styled.div`
  background-color: ${COLORS.BORDER_GREY};
  position: relative;
  border-radius: 10px;
  background: url(${(props) => props.bgImage}) center/cover no-repeat;
  // box-shadow: 0 0 10px ${COLORS.INPUT_GRAY};
`;
const Grid = styled.div`
  margin-top: 1em;
  margin-left: 1em;
  margin-right: 1em;
`;
const Row = styled.section`
  display: grid;
  /* grid-template-columns: 1fr 3fr; */
  grid-column-gap: 1em;
`;
const Aside = styled.aside`
  margin-left: auto;
`;
const Main = styled.section`
  background-color: ${COLORS.PRIMARY_WHITE};
  min-height: 200px;
  max-height: ${({ isHeight }) => (isHeight === "plain" ? "78vh" : "68vh")};
  height: 100vh;
  position: relative;
  overflow-y: auto;
  scrollbar-width: thin;
  border-radius: 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  box-shadow: 0 0 5px ${COLORS.BTN_GREEN};
`;

const Watermark = styled.div`
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  user-select: none;
  z-index: 10;
  border-radius: 10px;
`;

const WatermarkLogo = styled.img`
  width: clamp(300px, 95vw, 570px);
  height: clamp(250px, 70vh, 500px);
  opacity: 0.08;
  object-fit: contain;
`;

const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e1e3ee;
  border-radius: 10px 10px 0 0;
`;

const SearchInfo = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #acb1c2;
  white-space: nowrap;
  padding-right: 8px;
`;
